// Warning: this file contains 2 versions: one that hides unavailable widgets from the navigation, and one that shows them as disabled (default).
import { defineStore } from 'pinia';
import { useAccountStore } from '@/stores/account';
import { useUserStore } from '@/stores/user';

export const useCompanyWidgetsStore = defineStore('companyWidgets', {
    state: () => {
        const accountStore = useAccountStore();
        const accountId = accountStore?.account?.account_id ? accountStore.account.account_id : null;
        const userProspectDetailsSettings = useUserStore().getProspectDetailsSettings(accountId);
        const availableWidgets = [
            {
                id: 'prospect_sessions',
                component: 'ProspectSessions',
                props: { defaultToggle: true, auto: false, paywall: true, titleKey: 'components.prospect_sessions.name' },
            },
            {
                id: 'prospect_ai_summary',
                component: 'ProspectAiWidget',
                props: { slug: 'summary', defaultToggle: false, auto: false, titleKey: 'components.prospect_ai.models.summary' },
            },
            {
                id: 'prospect_firmology',
                component: 'ProspectFirmologyAlt',
                props: { defaultToggle: false, titleKey: 'components.prospect_firmology.name' },
            },
            {
                id: 'prospect_contact_information',
                component: 'ProspectContactInfo',
                props: { defaultToggle: true, titleKey: 'components.prospect_contact_information.name' },
            },
            {
                id: 'prospect_owner',
                component: 'ProspectAssignment',
                props: { defaultToggle: false, paywall: true, titleKey: 'components.prospect_owner.name' },
            },
            {
                id: 'prospect_tags',
                component: 'ProspectTags',
                props: { defaultToggle: false, auto: false, titleKey: 'components.prospect_tags.name' },
            },
            {
                id: 'prospect_events',
                component: 'ProspectEvents',
                props: { defaultToggle: true, auto: false, titleKey: 'components.prospect_events.name' },
            },
            // {
            //     id: 'prospect_pageviews',
            //     component: 'ProspectPageViews',
            //     props: { defaultToggle: true, auto: false, paywall: true, titleKey: 'components.prospect_pageviews.name' },
            // },
            // {
            //     id: 'prospect_sessions',
            //     component: 'ProspectSessions',
            //     props: { defaultToggle: true, auto: false, paywall: true, titleKey: 'components.prospect_sessions.name' },
            // },
            {
                id: 'prospect_contacts',
                component: 'ProspectContacts',
                props: { defaultToggle: true, auto: false, titleKey: 'components.prospect_contacts.name' },
            },
            {
                id: 'prospect_people',
                component: 'ProspectPeople',
                props: { defaultToggle: false, auto: false, paywall: true, titleKey: 'components.prospect_people.name' },
            },
            {
                id: 'prospect_ratings',
                component: 'ProspectReviewsSummaryAlt',
                props: { defaultToggle: false, titleKey: 'components.prospect_ratings.name' },
            },
            {
                id: 'prospect_opening_hours',
                component: 'ProspectOpeningHoursAlt',
                props: { defaultToggle: false, titleKey: 'components.prospect_opening_hours.name' },
            },
            {
                id: 'prospect_industries',
                component: 'ProspectIndustries',
                props: { defaultToggle: false, titleKey: 'components.prospect_industries.name' },
            },
            {
                id: 'prospect_ai_fundamentals',
                component: 'ProspectAiWidget',
                props: { slug: 'fundamentals', defaultToggle: false, auto: false, paywall: true, titleKey: 'components.prospect_ai.models.fundamentals' },
            },
            {
                id: 'prospect_sitesearch',
                component: 'ProspectSiteSearch',
                props: { defaultToggle: true, auto: false, titleKey: 'components.prospect_sitesearch.name' },
            },
            {
                id: 'prospect_family',
                component: 'ProspectFamily',
                props: { defaultToggle: true, auto: true, titleKey: 'components.prospect_family.name' },
            },
            {
                id: 'prospect_property',
                component: 'ProspectProperty',
                props: { defaultToggle: false, auto: false, titleKey: 'components.prospect_property.name' },
            },
            {
                id: 'prospect_same_address',
                component: 'ProspectSameAddress',
                props: { defaultToggle: false, auto: true, titleKey: 'components.prospect_same_address.name' },
            },
            {
                id: 'prospect_reviews',
                component: 'ProspectReviewsDetails',
                props: { defaultToggle: false, titleKey: 'components.prospect_reviews.name' },
            },
            {
                id: 'prospect_news',
                component: 'ProspectNews',
                props: { defaultToggle: true, auto: false, paywall: true, titleKey: 'components.prospect_news.name' },
            },
            {
                id: 'prospect_vacancies',
                component: 'ProspectVacancies',
                props: { defaultToggle: true, auto: false, paywall: true, titleKey: 'components.prospect_vacancies.name' },
            },
            {
                id: 'prospect_apps',
                component: 'ProspectAppsDetails',
                props: { defaultToggle: false, titleKey: 'components.prospect_apps.name' },
            },
            {
                id: 'prospect_siterank',
                component: 'ProspectSiteRank',
                props: { defaultToggle: false, auto: false, titleKey: 'components.prospect_siterank.name' },
            },
        ];

        const defaultOptions = {
            widgets: availableWidgets.map(widget => ({
                id: widget.id,
                defaultToggle: widget.props.defaultToggle || false
            }))
        };

        let mergedOptions;
        if (userProspectDetailsSettings?.options?.widgets) {
            const userWidgets = userProspectDetailsSettings.options.widgets || [];

            const availableIds = availableWidgets.map(w => w.id);
            const filteredUserWidgets = userWidgets.filter(w => availableIds.includes(w.id));

            const userWidgetIds = filteredUserWidgets.map(w => w.id);
            const missingWidgets = defaultOptions.widgets.filter(w => !userWidgetIds.includes(w.id));

            const missingSessions = missingWidgets.filter(w => w.id.includes('prospect_sessions'));
            const otherMissing = missingWidgets.filter(w => !w.id.includes('prospect_sessions'));

            mergedOptions = {
                widgets: [...missingSessions, ...filteredUserWidgets, ...otherMissing]
            };
        } else {
            mergedOptions = { ...defaultOptions };
        }

        return {
            componentRefs: {},
            options: mergedOptions,
            availableWidgets: availableWidgets
        }
    },
    actions: {
        saveProspectDetailsSettings() {
            const userStore = useUserStore();
            const accountStore = useAccountStore();
            userStore.saveProspectDetailsSettings(accountStore.account.account_id, { options: this.options });
        },
        getWidgetOrder() {
            if (this.options.widgets && Array.isArray(this.options.widgets)) {
                return this.options.widgets.map(widget => widget.id);
            }
            return this.availableWidgets.map(widget => widget.id);
        },
        setWidgetOrder(newOrder) {
            if (!Array.isArray(newOrder)) return false;
            
            const allWidgetIds = this.availableWidgets.map(widget => widget.id);
            const isValidOrder = newOrder.every(id => allWidgetIds.includes(id)) && 
                                allWidgetIds.every(id => newOrder.includes(id));
            
            if (!isValidOrder) return false;
            
            const currentWidgets = this.options.widgets || this.availableWidgets.map(widget => ({
                id: widget.id,
                defaultToggle: widget.props.defaultToggle || false
            }));
            
            this.options.widgets = newOrder.map(id => {
                const existingWidget = currentWidgets.find(w => w.id === id);
                return existingWidget || {
                    id,
                    defaultToggle: this.availableWidgets.find(w => w.id === id)?.props?.defaultToggle || false
                };
            });
            
            this.saveProspectDetailsSettings();
            return true;
        },
        // Warning: read comments on top.
        // moveWidgetUp(widgetId) {
        //     const order = this.getWidgetOrder();
        //     const index = order.indexOf(widgetId);
        //     if (index <= 0) return false;

        //     const hiddenIds = Object.entries(this.componentRefs)
        //         .filter(([id, comp]) => {
        //             if (id === 'prospect_contacts') return false;
        //             return comp && typeof comp.hide === 'boolean' && comp.hide;
        //         })
        //         .map(([id]) => id);

        //     let targetIndex = index - 1;
        //     while (targetIndex >= 0 && hiddenIds.includes(order[targetIndex])) {
        //         targetIndex--;
        //     }
        //     if (targetIndex < 0) return false;

        //     const newOrder = [...order];
        //     const [removed] = newOrder.splice(index, 1);
        //     newOrder.splice(targetIndex, 0, removed);

        //     const result = this.setWidgetOrder(newOrder);
        //     this.$patch({});
        //     return result;
        // },
        // moveWidgetDown(widgetId) {
        //     const order = this.getWidgetOrder();
        //     const index = order.indexOf(widgetId);
        //     if (index === -1 || index >= order.length - 1) return false;

        //     const hiddenIds = Object.entries(this.componentRefs)
        //         .filter(([id, comp]) => {
        //             if (id === 'prospect_contacts') return false;
        //             return comp && typeof comp.hide === 'boolean' && comp.hide;
        //         })
        //         .map(([id]) => id);

        //     let targetIndex = index + 1;
        //     while (targetIndex < order.length && hiddenIds.includes(order[targetIndex])) {
        //         targetIndex++;
        //     }
        //     if (targetIndex >= order.length) return false;

        //     const newOrder = [...order];
        //     const [removed] = newOrder.splice(index, 1);
        //     if (targetIndex > index) targetIndex--;
        //     newOrder.splice(targetIndex + 1, 0, removed);

        //     const result = this.setWidgetOrder(newOrder);
        //     this.$patch({});
        //     return result;
        // },
        moveWidgetUp(widgetId) {
            const order = this.getWidgetOrder();
            const index = order.indexOf(widgetId);
            if (index > 0) {
                const newOrder = [...order];
                [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                const result = this.setWidgetOrder(newOrder);
                
                // Force reactive update of visible widgets
                this.$patch({});
                
                return result;
            }
            return false;
        },
        
        moveWidgetDown(widgetId) {
            const order = this.getWidgetOrder();
            const index = order.indexOf(widgetId);
            if (index >= 0 && index < order.length - 1) {
                const newOrder = [...order];
                [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                const result = this.setWidgetOrder(newOrder);
                
                // Force reactive update of visible widgets
                this.$patch({});
                
                return result;
            }
            return false;
        },
        getWidgetToggle(widgetId) {
            if (this.options?.widgets) {
                const widgetConfig = this.options.widgets.find(w => w.id === widgetId);
                if (widgetConfig && 'defaultToggle' in widgetConfig) {
                    return widgetConfig.defaultToggle;
                }
            }
            const widget = this.availableWidgets.find(w => w.id === widgetId);
            return widget ? (widget.props.defaultToggle || false) : false;
        },
        setWidgetToggle(widgetId, isToggled) {
            if (!this.options.widgets) {
                this.options.widgets = this.availableWidgets.map(widget => ({
                    id: widget.id,
                    defaultToggle: widget.props.defaultToggle || false
                }));
            }
            
            const widgetIndex = this.options.widgets.findIndex(w => w.id === widgetId);
            
            if (widgetIndex >= 0) {
                this.options.widgets[widgetIndex] = {
                    ...this.options.widgets[widgetIndex],
                    defaultToggle: !!isToggled
                };
            } else if (this.availableWidgets.some(w => w.id === widgetId)) {
                this.options.widgets.push({
                    id: widgetId,
                    defaultToggle: !!isToggled
                });
            } else {
                return false;
            }
            
            this.saveProspectDetailsSettings();
            return true;
        },
        resetWidgetConfig() {
            this.options.widgets = this.availableWidgets.map(widget => ({
                id: widget.id,
                defaultToggle: widget.props.defaultToggle || false
            }));
            this.saveProspectDetailsSettings();
        },
        registerComponentRef(widgetId, componentRef) {
            if (componentRef) {
                this.componentRefs[widgetId] = componentRef;
            } else {
                delete this.componentRefs[widgetId];
            }
        },
        getComponentRef(widgetId) {
            return this.componentRefs[widgetId];
        },
        getAllComponentRefs() {
            return this.componentRefs;
        },
        getVisibleWidgets() {
            const widgetOrder = this.getWidgetOrder();
            return widgetOrder.map(id => {
                const component = this.componentRefs[id];
                const widgetDef = this.availableWidgets.find(w => w.id === id);
                const widgetSection = component?.$refs?.WidgetSection;

                const isHidden = id !== 'prospect_contacts'
                    && component
                    && typeof component.hide === 'boolean'
                    && component.hide;

                return {
                    id,
                    titleKey: widgetDef?.props?.titleKey || id,
                    hidden: !!isHidden,
                    loading: widgetSection ? !!widgetSection.loading : false,
                    loaded: widgetSection ? !!widgetSection.loaded : false,
                    toggle: widgetSection ? !!widgetSection.toggle : false,
                };
            });
        },
        // Warning: read comments on top.
        // getVisibleWidgets() {
        //     const visibleWidgets = Object.entries(this.componentRefs)
        //         .filter(([id, component]) => {
        //             if (id === 'prospect_contacts') return true;
        //             return component && typeof component.hide === 'boolean' && !component.hide;
        //         })
        //         .map(([id, component]) => {
        //             const widgetDef = this.availableWidgets.find(w => w.id === id);
        //             const widgetSection = component.$refs?.WidgetSection;
                    
        //             return {
        //                 id,
        //                 titleKey: widgetDef?.props?.titleKey || id,
        //                 loading: widgetSection ? !!widgetSection.loading : false,
        //                 loaded: widgetSection ? !!widgetSection.loaded : false,
        //                 toggle: widgetSection ? !!widgetSection.toggle : false,
        //             };
        //         });
            
        //     const widgetOrder = this.getWidgetOrder();
        //     return visibleWidgets.sort((a, b) => {
        //         const indexA = widgetOrder.indexOf(a.id);
        //         const indexB = widgetOrder.indexOf(b.id);
                
        //         if (indexA === -1) return 1;
        //         if (indexB === -1) return -1;
                
        //         return indexA - indexB;
        //     });
        // },
        getWidgets() {
            const widgetOrder = this.getWidgetOrder();
            const orderedWidgets = [...this.availableWidgets]
                .sort((a, b) => {
                    const indexA = widgetOrder.indexOf(a.id);
                    const indexB = widgetOrder.indexOf(b.id);                    
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    
                    return indexA - indexB;
                })
                .map(widget => ({
                    ...widget,
                    props: {
                        ...widget.props,
                        defaultToggle: this.getWidgetToggle(widget.id),
                        cssClassWidgetSection: 'content-type-15-narrow',
                        narrowVersion: true,
                        showLoadingToasts: false,
                    }
                }));
                
            return orderedWidgets;
        },
        navigateToWidget(id, offset = 127) {
            const componentRef = this.getComponentRef(id);
            if (!componentRef) return;
            
            const widgetSection = componentRef.$refs?.WidgetSection;

            if(widgetSection && widgetSection.toggle === false && typeof widgetSection.toggleSection === 'function') {
                widgetSection.toggleSection();
            }

            const element = componentRef.$el;
            if (!element) return;
            
            try {
                const scrollContainer = this.findScrollableParent(element);
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const relativeTop = elementRect.top - containerRect.top;

                setTimeout(() => {
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollTop + relativeTop - offset,
                        behavior: 'smooth'
                    });
                }, 50);
            } catch (e) {
                console.error('Error during scroll:', e);
            }
        },
        closeWidget(id) {
            const componentRef = this.getComponentRef(id);
            if (!componentRef) return;
            
            const widgetSection = componentRef.$refs?.WidgetSection;

            if(widgetSection && widgetSection.toggle === true && typeof widgetSection.toggleSection === 'function') {
                widgetSection.toggleSection();
            }
        },
        findScrollableParent(node) {
            if (node == null) {
                return document.documentElement;
            }
        
            if (node.scrollHeight > node.clientHeight) {
                return node;
            } else {
                return this.findScrollableParent(node.parentNode);
            }
        },
        resetWidgetStore() {
            this.componentRefs = {};
            this.$reset();
        }
    }
});
